/**
 * Cygnus icon helpers for code paths that build DOM/HTML outside Vue's
 * render tree — Tabulator cell formatters/filters (which return raw HTML
 * strings or imperatively-built DOM nodes) and any other non-Vue consumer.
 * Vue templates should use `<CygnusIcon>`
 * (`@apexl/icarus-core/src/components/icons/CygnusIcon.vue`) directly
 * instead of these.
 *
 * Both helpers reference the same JS-injected SVG sprite `<CygnusIcon>`
 * uses (`@apexl/icarus-core/src/services/icons/injectSprite`), so icons
 * rendered via either path share one sprite instance and stay visually
 * identical. `name` is run through the same `resolveIconName` resolution
 * (exact Cygnus name -> legacy FA alias -> style-prefix-stripped retry) that
 * `<CygnusIcon>` uses, so callers can pass a canonical name, a legacy FA
 * name, or a raw `'fal fa-server'`-style class string — some of these call
 * sites (Tabulator `buttonColumn`'s `icon` param in particular) carry
 * dynamic/config-driven names, not just hardcoded ones. An unresolved name
 * renders nothing (empty `<svg>`, no broken `<use>` reference) rather than
 * throwing, matching `<CygnusIcon>`'s own degrade behaviour.
 */
import injectSprite from '@apexl/icarus-core/src/services/icons/injectSprite.mjs';
import resolveIconName from '@apexl/icarus-core/src/services/icons/resolveIconName.mjs';
import warnUnknownIcon from '@apexl/icarus-core/src/services/icons/warnUnknownIcon.mjs';

const SVG_NS = 'http://www.w3.org/2000/svg';

function weightSuffix(variant) {
  return variant === 'solid' ? 's' : 'l';
}

function resolve(name) {
  const resolved = resolveIconName(name);
  if (!resolved) warnUnknownIcon(name);
  return resolved;
}

/** HTML string form — for cell formatters that return a markup string. */
export function cygnusIconHtml(name, { variant = 'light', spin = false, className = '', title = '' } = {}) {
  const resolved = resolve(name);
  if (!resolved) return '';
  injectSprite();
  const classes = ['cygnus-icon', spin ? 'cygnus-icon--spin' : '', className].filter(Boolean).join(' ');
  const titleAttr = title ? ` title="${String(title).replace(/"/g, '&quot;')}"` : '';
  return `<svg class="${classes}" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"${titleAttr}><use href="#i-${resolved}-${weightSuffix(variant)}"></use></svg>`;
}

/** DOM element form — for cell formatters/filters that build nodes imperatively. */
export function cygnusIconElement(name, { variant = 'light', spin = false, className = '', title = '' } = {}) {
  const resolved = resolve(name);
  injectSprite();
  const svg = document.createElementNS(SVG_NS, 'svg');
  const classes = ['cygnus-icon', spin ? 'cygnus-icon--spin' : '', className].filter(Boolean).join(' ');
  svg.setAttribute('class', classes);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '1em');
  svg.setAttribute('height', '1em');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  if (title) svg.setAttribute('title', title);

  if (resolved) {
    const use = document.createElementNS(SVG_NS, 'use');
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#i-${resolved}-${weightSuffix(variant)}`);
    use.setAttribute('href', `#i-${resolved}-${weightSuffix(variant)}`);
    svg.appendChild(use);
  }

  return svg;
}
